import * as React from 'react';

import type {
  AdminAlertDto,
  DashboardCampaignRowDto,
  ProfileMatrixRowDto,
} from '@/api/operational-view-client';
import {
  useCampaignsDashboard,
  useCards,
  useHealth,
  useProfileMatrix,
} from '@/api/use-operational-view';
import type { PendingActivationAccountDto } from '@/api/admin-activation-alerts-client';
import { useAdminPendingActivations } from '@/api/use-admin-activation-alerts';
import { useSupplierMonitoring } from '@/api/use-supplier-monitoring';
import { Button } from '@/components/ui/button';
import { Panel, StatusDot } from '@/components/ui/panel';
import {
  campaignStateInBucket,
  campaignStatusBucketLabel,
  CAMPAIGN_STATUS_BUCKETS,
  isCampaignStatusBucket,
  type CampaignStatusBucket,
} from '@/lib/campaign-labels';
import {
  CriticalityBadge,
  CycleDeadline,
  formatShortDate,
  MINUS,
  plural,
  REGISTRY_STATE,
  SortableHeader,
  StatusLabel,
  type SortState,
} from '@/routes/admin/campaign-registry-format';
import { HORS_JEUX_HINT } from '@/lib/operational-view-labels';
import { AnnouncementsDashboardAlert } from '@/routes/admin/communication/announcements-dashboard-alert';

// =============================================================================
// admin-dashboard-screen.tsx — Écran A-02 « Tableau de bord macroscopique Campagnes » (V-DASH).
// =============================================================================
// HOME GLOBAL cross-campagnes (distinct de A4 « Suivi de campagne temps réel », PAR campagne). V-DASH
// consomme UN SEUL endpoint `GET /admin/dashboard/campaigns` (BC analytics, AdminSessionGuard, withAdmin,
// fail-closed) qui compose synthèse + registre enrichi + criticité en UN round-trip (pas de fan-out N+1).
//
// Mise en page (maquette John 2026-09-16) : une seule colonne, largeur max 1400 px, séquence
// KPI → registre → alertes.
//   Zone 1 — Synthèse : bande de 5 cellules (Total, En cours, Brouillon + ratio, Clôturée, Annulée).
//   Zone 2 — Registre : intitulé (+ badge de criticité), statut (pastille + texte), créée le, échéances
//            (J−N / +N j de retard), volumétrie, « Ouvrir ». Triable (aria-sort), filtrable par statut.
//            Volet Focus Campagne (DBU §2.5 l.1053/1071) = LIGNE DÉPLIÉE : clic sur la ligne (ou bouton
//            chevron au clavier) ⇒ progression par famille et profil, cycle actif, analyse comportementale.
//   Zone 3 — Alertes actives : lignes plates cliquables (campagnes en cours uniquement, filtrage serveur).
//
// Autorité = API. L'UI LIT et habille ; aucune règle métier ici (criticité dérivée serveur de computeAlerts).
//
// WCAG 2.1 AA (ST-NFR-03) : <h1> unique ; <h2> par zone (aria-labelledby) ; statuts/alertes/criticité
// doublés d'un LIBELLÉ TEXTE (la pastille/couleur n'est qu'un renfort) ; table <caption>/<th scope>/tri
// boutonné + aria-sort ; ligne dépliable via bouton aria-expanded ; chargement/erreur en role status/alert.

export interface AdminDashboardScreenProps {
  /** Ouvre le détail d'une campagne (navigation portée par l'adaptateur routeur). */
  readonly onOpen: (campaignId: string) => void;
}

export function AdminDashboardScreen({
  onOpen,
}: AdminDashboardScreenProps): React.JSX.Element {
  const dashboard = useCampaignsDashboard();
  const campaigns = dashboard.data?.campaigns;
  const criticalAlertCount = (campaigns ?? []).reduce(
    (n, c) => n + c.alerts.filter((a) => a.severity === 'critical').length,
    0,
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6">
      <header className="space-y-1">
        <h1 className="text-[30px] font-medium tracking-tight">
          Tableau de bord administrateur
        </h1>
        <p className="text-muted-foreground">
          Toutes campagnes confondues
          {campaigns !== undefined
            ? ` · ${plural(campaigns.length, 'campagne')} · ${plural(
                criticalAlertCount,
                'alerte critique',
                'alertes critiques',
              )}`
            : null}
        </p>
      </header>

      <SummarySection dashboard={dashboard} />
      <RegistrySection dashboard={dashboard} onOpen={onOpen} />
      <AlertsSection dashboard={dashboard} onOpen={onOpen} />
    </div>
  );
}

type DashboardQuery = ReturnType<typeof useCampaignsDashboard>;

// =============================================================================
// Zone 1 — Synthèse (bande de 5 cellules).
// =============================================================================

function SummarySection({
  dashboard,
}: {
  readonly dashboard: DashboardQuery;
}): React.JSX.Element {
  const summary = dashboard.data?.summary;

  return (
    <section aria-labelledby="kpi-heading">
      <h2 id="kpi-heading" className="sr-only">
        Indicateurs de synthèse
      </h2>

      {dashboard.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement des indicateurs…
        </p>
      ) : dashboard.isError ? (
        <p role="alert" className="text-destructive">
          Erreur de chargement des indicateurs : {dashboard.error.message}
        </p>
      ) : summary !== undefined ? (
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-5">
          <KpiCell label="Total campagnes" value={summary.totalCount} />
          <KpiCell
            label={campaignStatusBucketLabel('en-cours')}
            value={summary.openCount}
          />
          {/* DBU §2.5 l.1063 — RATIO BROUILLON (calcul serveur) : plus affiché à côté du compteur (retour John
              2026-09-16), conservé en infobulle et pour les lecteurs d'écran. */}
          <KpiCell
            label={campaignStatusBucketLabel('brouillon')}
            value={summary.draftCount}
            hint={`Ratio brouillon : ${String(summary.draftRatioPct)} %`}
          />
          <KpiCell
            label={campaignStatusBucketLabel('cloturee')}
            value={summary.closedCount}
          />
          <KpiCell
            label={campaignStatusBucketLabel('annulee')}
            value={summary.cancelledCount}
            className="col-span-2 sm:col-span-1"
          />
        </div>
      ) : null}
    </section>
  );
}

/** Cellule de la bande KPI : libellé mono + valeur (grisée à zéro) ; `hint` en infobulle + texte sr-only. */
function KpiCell({
  label,
  value,
  hint,
  className,
}: {
  readonly label: string;
  readonly value: number;
  readonly hint?: string;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <div className={`bg-card px-6 py-4 ${className ?? ''}`} title={hint}>
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 flex items-baseline gap-2">
        <span
          className={`text-3xl font-semibold tabular-nums ${
            value === 0 ? 'text-muted-foreground/60' : 'text-foreground'
          }`}
        >
          {value}
        </span>
        {hint !== undefined ? <span className="sr-only">{hint}</span> : null}
      </p>
    </div>
  );
}

// =============================================================================
// Zone 2 — Registre (triable, filtrable, lignes dépliables = volet Focus Campagne).
// =============================================================================

type SortKey = 'intitule' | 'state' | 'createdAt';

/** Nombre de colonnes du registre (le volet déplié les couvre toutes). */
const REGISTRY_COLUMN_COUNT = 6;

/** Comparateur d'une colonne (ordre croissant ; le sens est appliqué par l'appelant). */
function compareByKey(
  a: DashboardCampaignRowDto,
  b: DashboardCampaignRowDto,
  key: SortKey,
): number {
  switch (key) {
    case 'intitule':
      return a.intitule.localeCompare(b.intitule, 'fr');
    case 'state':
      return REGISTRY_STATE[a.state].label.localeCompare(
        REGISTRY_STATE[b.state].label,
        'fr',
      );
    case 'createdAt':
      return a.createdAt.localeCompare(b.createdAt);
  }
}

function RegistrySection({
  dashboard,
  onOpen,
}: {
  readonly dashboard: DashboardQuery;
  readonly onOpen: (campaignId: string) => void;
}): React.JSX.Element {
  // Tri client sur les données déjà chargées. Défaut : plus récentes d'abord (parité registre A-12).
  const [sort, setSort] = React.useState<SortState<SortKey>>({
    key: 'createdAt',
    dir: 'desc',
  });
  // DBU §2.5 l.1069 — filtrage par STATUT OPÉRATIONNEL (4 buckets métier + « tous »). State in-memory.
  const [statusFilter, setStatusFilter] =
    React.useState<CampaignStatusBucket>('tous');
  // Volet Focus Campagne : une seule ligne dépliée à la fois.
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const loadedCount = dashboard.data?.campaigns.length ?? 0;

  const rows = React.useMemo(() => {
    const data = dashboard.data?.campaigns;
    if (data === undefined) {
      return [];
    }
    const filtered = data.filter((c) =>
      campaignStateInBucket(c.state, statusFilter),
    );
    const sorted = [...filtered].sort((a, b) => compareByKey(a, b, sort.key));
    return sort.dir === 'asc' ? sorted : sorted.reverse();
  }, [dashboard.data, sort, statusFilter]);

  const onSort = React.useCallback((key: SortKey): void => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'createdAt' ? 'desc' : 'asc' },
    );
  }, []);

  const onToggle = React.useCallback((campaignId: string): void => {
    setExpandedId((prev) => (prev === campaignId ? null : campaignId));
  }, []);

  const ready = !dashboard.isPending && !dashboard.isError;

  return (
    <Panel
      headingId="registry-heading"
      title="Registre des opérations"
      count={ready ? rows.length : undefined}
      aside={
        ready ? (
          <>
            <label htmlFor="dashboard-status-filter">Statut</label>
            <select
              id="dashboard-status-filter"
              name="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                const next = e.target.value;
                if (isCampaignStatusBucket(next)) {
                  setStatusFilter(next);
                }
              }}
              className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {CAMPAIGN_STATUS_BUCKETS.map((bucket) => (
                <option key={bucket} value={bucket}>
                  {campaignStatusBucketLabel(bucket)}
                </option>
              ))}
            </select>
          </>
        ) : undefined
      }
    >
      {dashboard.isPending ? (
        <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
          Chargement des campagnes…
        </p>
      ) : dashboard.isError ? (
        <p role="alert" className="px-6 py-4 text-sm text-destructive">
          Erreur de chargement : {dashboard.error.message}
        </p>
      ) : loadedCount === 0 ? (
        <p
          role="status"
          className="px-6 py-10 text-center text-sm text-muted-foreground"
        >
          Aucune campagne pour le moment.
        </p>
      ) : rows.length === 0 ? (
        <p
          role="status"
          className="px-6 py-10 text-center text-sm text-muted-foreground"
        >
          Aucune campagne pour le statut «{' '}
          {campaignStatusBucketLabel(statusFilter)} ».
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Registre des campagnes (intitulé et criticité, statut, date de création,
              échéances, volumétrie) — colonnes triables ; chaque ligne se déplie pour
              afficher le suivi de la campagne
            </caption>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <SortableHeader
                  label="Intitulé"
                  sortKey="intitule"
                  sort={sort}
                  onSort={onSort}
                  className="pl-6"
                />
                <SortableHeader
                  label="Statut"
                  sortKey="state"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Créée le"
                  sortKey="createdAt"
                  sort={sort}
                  onSort={onSort}
                />
                <th scope="col" className="px-4 py-3">
                  Échéances
                  <span className="normal-case tracking-normal text-dash-late">
                    {' '}
                    · retard en rouge
                  </span>
                </th>
                <th scope="col" className="px-4 py-3">
                  Volumétrie
                </th>
                <th scope="col" className="py-3 pl-4 pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <RegistryRow
                  key={row.campaignId}
                  row={row}
                  isExpanded={row.campaignId === expandedId}
                  onOpen={onOpen}
                  onToggle={onToggle}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function RegistryRow({
  row,
  isExpanded,
  onOpen,
  onToggle,
}: {
  readonly row: DashboardCampaignRowDto;
  readonly isExpanded: boolean;
  readonly onOpen: (campaignId: string) => void;
  readonly onToggle: (campaignId: string) => void;
}): React.JSX.Element {
  const panelId = `focus-panel-${row.campaignId}`;
  const isCancelled = row.state === 'Abandonnee';
  // Compte à rebours / retard pertinent tant que la campagne est en cours (ni brouillon, ni close, ni abandonnée).
  const showCountdown = campaignStateInBucket(row.state, 'en-cours');
  const rail = isExpanded
    ? 'shadow-[inset_3px_0_0_0_hsl(var(--foreground))]'
    : '';

  return (
    <>
      <tr
        // Clic souris n'importe où sur la ligne = déplier/replier le volet. L'équivalent clavier est le
        // bouton chevron de la 1re cellule (aria-expanded) ; les boutons internes stoppent la propagation.
        onClick={() => onToggle(row.campaignId)}
        className={`cursor-pointer border-b border-border last:border-b-0 ${
          isExpanded ? 'bg-dash-band' : ''
        } ${isCancelled ? 'text-dash-muted-row' : ''}`}
      >
        <td className={`min-w-[12rem] py-3.5 pl-6 pr-4 ${rail}`}>
          <div className="flex items-baseline gap-2">
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? panelId : undefined}
              aria-label={`Afficher le suivi de la campagne ${row.intitule}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(row.campaignId);
              }}
              className={
                isExpanded
                  ? 'text-xs text-muted-foreground'
                  : 'sr-only focus:not-sr-only focus:text-xs focus:text-muted-foreground'
              }
            >
              <span aria-hidden="true">▼</span>
            </button>
            <span className="min-w-0">
              {/* DBU §2.5 l.1075 — drill-down via l'INTITULÉ cliquable (en plus du bouton « Ouvrir »). Élément
                  INLINE (un <button> natif est un bloc rectangulaire) : sur un nom à 2 lignes, la zone cliquable
                  suit le texte au lieu de couvrir le rectangle englobant. Clavier : Entrée / Espace. */}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(row.campaignId);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpen(row.campaignId);
                  }
                }}
                aria-label={`Ouvrir le détail de la campagne ${row.intitule}`}
                className="cursor-pointer rounded-sm font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {row.intitule}
              </span>
              {row.criticality !== 'V' ? (
                <span className="ml-2 inline-block align-middle">
                  <CriticalityBadge criticality={row.criticality} />
                </span>
              ) : null}
            </span>
          </div>
        </td>
        <td className="px-4 py-3.5">
          <StatusLabel state={row.state} />
        </td>
        <td className="whitespace-nowrap px-4 py-3.5 tabular-nums">
          {formatShortDate(row.createdAt)}
        </td>
        <td className="px-4 py-3.5 tabular-nums">
          {row.cycles.length === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            row.cycles.map((cycle, i) => (
              <React.Fragment key={cycle.cycleNumber}>
                {i > 0 ? ' · ' : null}
                <CycleDeadline cycle={cycle} showCountdown={showCountdown} />
              </React.Fragment>
            ))
          )}
        </td>
        <td className="px-4 py-3.5 tabular-nums">
          {/* Retour à la ligne permis entre les deux mesures (jamais au milieu) : le tableau tient à 1280 px. */}
          <span className="whitespace-nowrap">
            {plural(row.invitedSupplierCount, 'fournisseur')}
          </span>{' '}
          · <span className="whitespace-nowrap">{plural(row.profileCount, 'profil')}</span>
        </td>
        <td className="py-3.5 pl-4 pr-6 text-right">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(row.campaignId);
            }}
            aria-label={`Ouvrir la campagne ${row.intitule}`}
            className={
              isExpanded
                ? 'min-w-20 border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background'
                : 'min-w-20 text-foreground'
            }
          >
            Ouvrir
          </Button>
        </td>
      </tr>
      {isExpanded ? (
        <tr id={panelId} className="border-b border-border bg-dash-band last:border-b-0">
          <td colSpan={REGISTRY_COLUMN_COUNT} className={`px-6 pb-6 pt-2 ${rail}`}>
            <FocusPanel campaign={row} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// =============================================================================
// Volet Focus Campagne (ligne dépliée) — réutilise les read-models santé/cards/matrice/suivi LIÉS.
// =============================================================================

function FocusPanel({
  campaign,
}: {
  readonly campaign: DashboardCampaignRowDto;
}): React.JSX.Element {
  // Le détail complet s'ouvre via « Ouvrir » ou l'intitulé : pas de lien redondant dans le volet.
  return (
    <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr_1fr]">
      <ProgressColumn campaignId={campaign.campaignId} />
      <CycleColumn campaign={campaign} />
      <BehaviorColumn campaignId={campaign.campaignId} />
    </div>
  );
}

/** Titre de colonne du volet (mono uppercase) ; porte le heading h3 de la sous-zone. */
function PanelHeading({
  id,
  children,
}: {
  readonly id: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <h3
      id={id}
      className="border-b border-border pb-2 font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
    >
      {children}
    </h3>
  );
}

/** Ligne libellé / valeur (mono, alignée à droite) d'une colonne du volet. */
function PanelStat({
  label,
  hint,
  children,
}: {
  readonly label: string;
  /** Sous-libellé discret (aide contextuelle), rendu dans le <dt> sous le libellé. */
  readonly hint?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-b-0">
      {/* Sans aide : <dt> texte seul (structure dt/dd lue telle quelle par les ST). */}
      {hint === undefined ? (
        <dt>{label}</dt>
      ) : (
        <dt>
          <span>{label}</span>
          <span className="block text-xs text-muted-foreground">{hint}</span>
        </dt>
      )}
      <dd className="font-mono tabular-nums">{children}</dd>
    </div>
  );
}

function PanelLoading({ label }: { readonly label: string }): React.JSX.Element {
  return (
    <p role="status" className="py-2 text-sm text-muted-foreground">
      {label}
    </p>
  );
}

function PanelError({
  label,
  error,
}: {
  readonly label: string;
  readonly error: Error;
}): React.JSX.Element {
  return (
    <p role="alert" className="py-2 text-sm text-destructive">
      {label} : {error.message}
    </p>
  );
}

/** Progression d'un profil (tous niveaux confondus). */
interface ProfileProgress {
  readonly profileCode: string;
  readonly levelCount: number;
  readonly progressPct: number;
}

/** Progression d'une famille métier + ses profils. */
interface FamilyProgress {
  readonly familyLabel: string;
  readonly progressPct: number;
  readonly profiles: readonly ProfileProgress[];
}

/** Libellé d'affichage d'une famille absente du catalogue (profil sans famille jointe). */
const UNKNOWN_FAMILY_LABEL = 'Famille non renseignée';

const mean = (values: readonly number[]): number =>
  values.length === 0
    ? 0
    : Math.round(values.reduce((s, v) => s + v, 0) / values.length);

/**
 * DBU §2.5 l.1071 — progression de réponses PAR FAMILLE ET PAR PROFIL. Regroupe les couples (profil × niveau)
 * de la matrice A4 par famille puis par profil (ordre de première apparition). Progression = MOYENNE des
 * `responseRatePct` des couples concernés : EXACTE (pas une heuristique) car le dénominateur (fournisseurs
 * invités) est CONSTANT par campagne ⇒ moyenne = cellules remplies / cellules attendues. Aucune règle métier
 * ajoutée : l'autorité des taux reste l'API. Un profil sans famille est regroupé sous « Famille non renseignée ».
 */
function progressTree(
  rows: readonly ProfileMatrixRowDto[],
): readonly FamilyProgress[] {
  const families = new Map<string, Map<string, number[]>>();
  for (const r of rows) {
    const familyLabel = r.family ?? UNKNOWN_FAMILY_LABEL;
    let profiles = families.get(familyLabel);
    if (profiles === undefined) {
      profiles = new Map();
      families.set(familyLabel, profiles);
    }
    const rates = profiles.get(r.profileCode);
    if (rates === undefined) {
      profiles.set(r.profileCode, [r.responseRatePct]);
    } else {
      rates.push(r.responseRatePct);
    }
  }
  return [...families].map(([familyLabel, profiles]) => ({
    familyLabel,
    progressPct: mean([...profiles.values()].flat()),
    profiles: [...profiles].map(([profileCode, rates]) => ({
      profileCode,
      levelCount: rates.length,
      progressPct: mean(rates),
    })),
  }));
}

/** Barre de progression décorative : le % en texte porte l'information (WCAG). */
function ProgressLine({
  label,
  hint,
  pct,
  indent,
}: {
  readonly label: string;
  readonly hint: string;
  readonly pct: number;
  readonly indent?: boolean;
}): React.JSX.Element {
  return (
    <li
      className={`grid grid-cols-[minmax(0,1fr)_minmax(4rem,10rem)_3.5rem] items-center gap-4 border-b border-border py-2 last:border-b-0 ${
        indent === true ? 'pl-6' : ''
      }`}
    >
      <span className="truncate">
        <span className={indent === true ? '' : 'font-medium'}>{label}</span>{' '}
        <span className="text-muted-foreground">{hint}</span>
      </span>
      <span aria-hidden="true" className="h-1.5 rounded-full bg-muted">
        <span
          className="block h-full rounded-full bg-success"
          style={{ width: `${String(Math.min(100, Math.max(0, pct)))}%` }}
        />
      </span>
      <span className="text-right font-mono tabular-nums">{pct} %</span>
    </li>
  );
}

function ProgressColumn({
  campaignId,
}: {
  readonly campaignId: string;
}): React.JSX.Element {
  const matrix = useProfileMatrix(campaignId, {});
  const tree = React.useMemo(
    () => progressTree(matrix.data?.rows ?? []),
    [matrix.data],
  );
  const headingId = `focus-progress-${campaignId}`;

  return (
    <section aria-labelledby={headingId}>
      <PanelHeading id={headingId}>Progression des réponses</PanelHeading>
      {matrix.isPending ? (
        <PanelLoading label="Chargement de la progression des réponses…" />
      ) : matrix.isError ? (
        <PanelError
          label="Erreur de chargement de la progression"
          error={matrix.error}
        />
      ) : tree.length === 0 ? (
        <p role="status" className="py-2 text-sm text-muted-foreground">
          Aucun profil au périmètre de cette campagne.
        </p>
      ) : (
        <ul className="max-h-72 overflow-y-auto">
          {tree.map((family) => (
            <React.Fragment key={family.familyLabel}>
              <ProgressLine
                label={family.familyLabel}
                hint={plural(family.profiles.length, 'profil')}
                pct={family.progressPct}
              />
              {family.profiles.map((p) => (
                <ProgressLine
                  key={`${family.familyLabel}-${p.profileCode}`}
                  label={p.profileCode}
                  hint={plural(p.levelCount, 'niveau', 'niveaux')}
                  pct={p.progressPct}
                  indent
                />
              ))}
            </React.Fragment>
          ))}
        </ul>
      )}
    </section>
  );
}

function CycleColumn({
  campaign,
}: {
  readonly campaign: DashboardCampaignRowDto;
}): React.JSX.Element {
  const health = useHealth(campaign.campaignId, {});
  const cards = useCards(campaign.campaignId, {});
  const headingId = `focus-cycle-${campaign.campaignId}`;

  const participationPct =
    health.data?.participationPct ?? campaign.participationPct;
  const submitted =
    health.data?.submittedSupplierCount ?? campaign.submittedSupplierCount;
  const invited =
    health.data?.invitedSupplierCount ?? campaign.invitedSupplierCount;

  return (
    <section aria-labelledby={headingId}>
      <PanelHeading id={headingId}>
        {campaign.currentCycleNumber === null
          ? 'Aucun cycle actif'
          : `Cycle ${String(campaign.currentCycleNumber)}`}
      </PanelHeading>
      <dl>
        <PanelStat label="Participation">
          {participationPct} %{' '}
          <span className="text-muted-foreground">
            · {submitted} / {invited}
          </span>
        </PanelStat>
        <PanelStat label="Cellules saisies">
          {cards.data === undefined ? (
            '…'
          ) : (
            <>
              {cards.data.collectedCellCount} / {cards.data.expectedCellCount}{' '}
              <span className="text-muted-foreground">
                ·{' '}
                {cards.data.expectedCellCount === 0
                  ? 0
                  : Math.round(
                      (cards.data.collectedCellCount /
                        cards.data.expectedCellCount) *
                        100,
                    )}{' '}
                %
              </span>
            </>
          )}
        </PanelStat>
        <PanelStat label="TJM hors jeu" hint={HORS_JEUX_HINT}>
          {health.data === undefined ? '…' : health.data.horsJeuxCount}
        </PanelStat>
      </dl>
    </section>
  );
}

/**
 * DBU §2.5 l.1071/1083 — analyse comportementale « invités non connectés vs connectés sans saisie » + seuil
 * « > 48 h ». RÉUTILISE le read-model V-SUIVI-FOURN (composition identity × rate-collection CÔTÉ API) ; le
 * seuil est appliqué serveur (drapeau `connectedNoSubmissionOver48h`), l'UI ne fait que compter.
 */
function BehaviorColumn({
  campaignId,
}: {
  readonly campaignId: string;
}): React.JSX.Element {
  const monitoring = useSupplierMonitoring(campaignId);
  const headingId = `focus-behavior-${campaignId}`;

  if (monitoring.isPending) {
    return (
      <section aria-labelledby={headingId}>
        <PanelHeading id={headingId}>Comportement</PanelHeading>
        <PanelLoading label="Chargement de l'analyse comportementale des fournisseurs…" />
      </section>
    );
  }
  if (monitoring.isError) {
    return (
      <section aria-labelledby={headingId}>
        <PanelHeading id={headingId}>Comportement</PanelHeading>
        <PanelError
          label="Erreur de chargement de l'analyse comportementale"
          error={monitoring.error}
        />
      </section>
    );
  }

  const summary = monitoring.data.behavioralSummary;
  const over48hCount = monitoring.data.rows.filter(
    (row) => row.connectedNoSubmissionOver48h,
  ).length;
  return (
    <section aria-labelledby={headingId}>
      <PanelHeading id={headingId}>
        Comportement · {plural(summary.invitedSupplierCount, 'invité')}
      </PanelHeading>
      <dl>
        <PanelStat label="Jamais connectés">
          {summary.invitedNotConnectedCount}
        </PanelStat>
        <PanelStat label="Connectés sans saisie">
          {summary.connectedNoSubmissionCount}
        </PanelStat>
        <PanelStat label="Sans saisie > 48 h">{over48hCount}</PanelStat>
      </dl>
    </section>
  );
}

// =============================================================================
// Zone 3 — Alertes actives (portées par les lignes, campagnes en cours ; cliquables).
// =============================================================================

/** Alerte de pilotage rattachée à sa campagne d'origine (contexte cross-campagnes). */
interface DashboardAlert {
  readonly campaignId: string;
  readonly intitule: string;
  readonly cycleNumber: number | null;
  readonly index: number;
  readonly alert: AdminAlertDto;
}

function AlertsSection({
  dashboard,
  onOpen,
}: {
  readonly dashboard: DashboardQuery;
  readonly onOpen: (campaignId: string) => void;
}): React.JSX.Element {
  // Agrégation cross-campagnes : les alertes viennent DÉJÀ des lignes (aucun fan-out getHealth).
  const alerts: readonly DashboardAlert[] = (dashboard.data?.campaigns ?? [])
    .flatMap((campaign) =>
      campaign.alerts.map((alert, index) => ({
        campaignId: campaign.campaignId,
        intitule: campaign.intitule,
        cycleNumber: campaign.currentCycleNumber,
        index,
        alert,
      })),
    )
    .sort((a, b) => {
      if (a.alert.severity !== b.alert.severity) {
        return a.alert.severity === 'critical' ? -1 : 1;
      }
      return a.alert.daysRemaining - b.alert.daysRemaining;
    });

  return (
    <div className="space-y-6">
      <Panel
        headingId="alerts-heading"
        title="Alertes actives"
        count={dashboard.isPending ? undefined : alerts.length}
        aside="Échéance proche ou participation basse · triées par urgence"
      >
        <div aria-live="polite" aria-busy={dashboard.isPending}>
          {dashboard.isPending ? (
            <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
              Chargement des alertes…
            </p>
          ) : alerts.length === 0 ? (
            <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
              Aucune alerte d'échéance ou de participation à signaler.
            </p>
          ) : (
            <ul>
              {alerts.map((entry) => (
                <AlertItem
                  key={`${entry.campaignId}-${String(entry.index)}`}
                  entry={entry}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          )}
        </div>
      </Panel>

      {/* §2.5 l.1085 (item 7, LOT-ALERTES, DEC-20260722-11) — TABLEAU NOMINATIF des comptes en attente. L'encart
          compteur §2.2 l.728 est retiré (arbitrage GATE_HUMAN 2026-09-16) : le tableau porte la même information. */}
      <PendingActivationAccountsTable />

      {/* R15-COM-03 (DBU l.1087/1245) — Information : annonces planifiées auto-annulées (campagne inéligible). */}
      <AnnouncementsDashboardAlert />
    </div>
  );
}

/**
 * Une alerte en ligne plate (DBU §2.5 l.1093 : cliquable → campagne d'origine). Sévérité en texte (sr-only,
 * la pastille n'est qu'un renfort) ; détail composé des champs structurés (jamais « J--N »).
 */
function AlertItem({
  entry,
  onOpen,
}: {
  readonly entry: DashboardAlert;
  readonly onOpen: (campaignId: string) => void;
}): React.JSX.Element {
  const { alert } = entry;
  const isCritical = alert.severity === 'critical';
  const overdue = alert.daysRemaining < 0;
  return (
    <li className="flex items-center gap-3 border-b border-border px-6 py-3.5 text-sm last:border-b-0">
      <span
        aria-hidden="true"
        className={`h-2 w-2 shrink-0 rounded-full ${isCritical ? 'bg-primary' : 'bg-primary/50'}`}
      />
      <span className="min-w-0 flex-1">
        <span className="sr-only">
          {isCritical ? 'Critique' : 'Avertissement'} :{' '}
        </span>
        <span className="font-medium">{entry.intitule}</span>{' '}
        <span className="text-muted-foreground">
          Participation {alert.participationPct} % · échéance{' '}
          {overdue ? (
            <span className="text-dash-late">
              dépassée de {-alert.daysRemaining} j
            </span>
          ) : (
            <>
              {entry.cycleNumber !== null
                ? `cycle ${String(entry.cycleNumber)} `
                : null}
              <span className="text-dash-late">
                J{MINUS}
                {alert.daysRemaining}
              </span>
            </>
          )}
        </span>
      </span>
      <button
        type="button"
        onClick={() => onOpen(entry.campaignId)}
        aria-label={`Ouvrir la campagne ${entry.intitule} concernée par cette alerte`}
        className="shrink-0 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        Ouvrir <span aria-hidden="true">→</span>
      </button>
    </li>
  );
}


/** Date + heure FR courtes « 12 sept. · 14:27 » (année hors année courante) ; « — » si invalide. */
function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const time = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${formatShortDate(iso)} · ${time}`;
}

/**
 * §2.5 l.1085 (item 7, LOT-ALERTES, DEC-20260722-11) — TABLEAU NOMINATIF des « Comptes en attente
 * d'activation » (token EXPIRÉ OU émis depuis plus de 48h). Vue ADMIN uniquement (endpoint AdminSessionGuard) :
 * le nominatif (SIRET fournisseur + email de contact + dates du token) est ADMIS sur ce SEUL chemin admin ;
 * aucun fournisseur n'accède ici (pas de fuite inter-fournisseur). AUTORITÉ = API : l'UI LIT et habille. Le
 * tableau ne se matérialise que si au moins une ligne est présente (chargement/erreur/vide ⇒ rien).
 *
 * ÉCART assumé (inv n°2 — isolation des schémas) : la « raison sociale » vit dans le BC supplier-reference,
 * PAS dans identity ; la surfacer exigerait une composition cross-BC hors périmètre LOT-ALERTES. Le SIRET
 * (identifiant fournisseur porté par identity) tient lieu d'identifiant d'entreprise ici.
 *
 * WCAG 2.1 AA : <caption> ; <th scope="col"> ; statut en LIBELLÉ TEXTE (la pastille n'est qu'un renfort).
 */
function PendingActivationAccountsTable(): React.JSX.Element | null {
  const query = useAdminPendingActivations();
  const data = query.data;
  if (data === undefined || data.accounts.length === 0) {
    return null;
  }
  const { accounts, thresholdHours, truncated } = data;
  return (
    <Panel
      headingId="pending-activation-heading"
      title="Comptes en attente d'activation"
      count={accounts.length}
      aside={`Lien d'activation expiré ou inutilisé au-delà de ${String(thresholdHours)} h`}
    >
      <div className="overflow-x-auto">
        <table className="ds-table w-full border-collapse text-sm">
          <caption className="sr-only">
            Comptes fournisseurs en attente d'activation : fournisseur (SIRET), contact,
            date d'émission et d'expiration du lien, statut du lien
          </caption>
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left">
              <th scope="col" className="py-3 pl-6 pr-4">
                Fournisseur (SIRET)
              </th>
              <th scope="col" className="px-4 py-3">
                Contact
              </th>
              <th scope="col" className="px-4 py-3">
                Lien émis le
              </th>
              <th scope="col" className="px-4 py-3">
                Expire le
              </th>
              <th scope="col" className="py-3 pl-4 pr-6">
                Statut du lien
              </th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <PendingActivationRow key={account.contactId} account={account} />
            ))}
          </tbody>
        </table>
      </div>
      {truncated ? (
        <p
          role="note"
          className="border-t border-border px-6 py-3 text-sm text-muted-foreground"
        >
          Liste tronquée : seuls les comptes les plus anciens sont affichés. Traitez ces
          comptes pour révéler les suivants.
        </p>
      ) : null}
    </Panel>
  );
}

/** Une ligne du tableau nominatif des comptes en attente : statut en LIBELLÉ TEXTE (WCAG). */
function PendingActivationRow({
  account,
}: {
  readonly account: PendingActivationAccountDto;
}): React.JSX.Element {
  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="py-3.5 pl-6 pr-4 font-medium tabular-nums">{account.siret}</td>
      <td className="px-4 py-3.5">{account.contactEmail}</td>
      <td className="whitespace-nowrap px-4 py-3.5 tabular-nums">
        {formatDateTime(account.issuedAt)}
      </td>
      <td className="whitespace-nowrap px-4 py-3.5 tabular-nums">
        {formatDateTime(account.expiresAt)}
      </td>
      <td className="py-3.5 pl-4 pr-6">
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          <StatusDot className={account.tokenExpired ? 'bg-dash-late' : 'bg-primary'} />
          {account.tokenExpired ? 'Lien expiré' : 'En attente > 48h'}
        </span>
      </td>
    </tr>
  );
}
